import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { COULEURS } from "./splashStyles.js";

/**
 * ============================================================================
 *  ListeDefilante.tsx — Une liste qui défile, et qui le dit
 * ----------------------------------------------------------------------------
 *  Sur téléphone, la feuille de navigation garde son plan toujours visible : seules
 *  les scènes du dessous défilent, dans ce conteneur. Une liste coupée par le
 *  bas du cadre ne se distingue pas d'une liste qui s'arrête là : un chevron
 *  en haut et un chevron en bas disent qu'il reste des lignes de ce côté. Ils
 *  apparaissent et disparaissent avec le défilement, et se touchent pour
 *  avancer d'une page.
 *
 *  Les chevrons ne sont pas dans l'ordre de tabulation : le défilement est déjà
 *  atteignable au clavier et au lecteur d'écran par les lignes elles-mêmes.
 * ==========================================================================*/

/** Marge de tolérance (px) : le sous-pixel ne doit pas allumer un chevron. */
const TOLERANCE = 2;

export function ListeDefilante({ children }: { children: ReactNode }) {
  const defile = useRef<HTMLDivElement>(null);
  const contenu = useRef<HTMLDivElement>(null);
  const [reste, setReste] = useState({ haut: false, bas: false });

  const mesurer = useCallback(() => {
    const el = defile.current;
    if (!el) return;
    const haut = el.scrollTop > TOLERANCE;
    const bas = el.scrollTop + el.clientHeight < el.scrollHeight - TOLERANCE;
    setReste((avant) => (avant.haut === haut && avant.bas === bas ? avant : { haut, bas }));
  }, []);

  // Le cadre change (rotation, clavier) ou le contenu change (sous-zone dépliée) : on remesure.
  useEffect(() => {
    mesurer();
    const observateur = new ResizeObserver(mesurer);
    if (defile.current) observateur.observe(defile.current);
    if (contenu.current) observateur.observe(contenu.current);
    return () => observateur.disconnect();
  }, [mesurer]);

  const avancer = (sens: -1 | 1) => {
    const el = defile.current;
    if (el) el.scrollBy({ top: sens * el.clientHeight * 0.8, behavior: "smooth" });
  };

  // Hauteur du contenu, jusqu'à la place que laisse la feuille : un flex en colonne plutôt
  // qu'un `height: 100%`, qui ne se résout pas sous une hauteur seulement plafonnée (il
  // rendrait toute la liste, sans défilement).
  return (
    <div style={{ position: "relative", flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        ref={defile}
        className="sn-defile"
        onScroll={mesurer}
        style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}
      >
        <div ref={contenu}>{children}</div>
      </div>
      <Chevron sens={-1} visible={reste.haut} onClick={() => avancer(-1)} />
      <Chevron sens={1} visible={reste.bas} onClick={() => avancer(1)} />
    </div>
  );
}

function Chevron({ sens, visible, onClick }: { sens: -1 | 1; visible: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-hidden
      tabIndex={-1}
      onClick={onClick}
      style={{
        ...chevron,
        ...(sens < 0
          ? { top: 0, background: "linear-gradient(to bottom, rgba(16,26,23,.95), rgba(16,26,23,0))" }
          : { bottom: 0, background: "linear-gradient(to top, rgba(16,26,23,.95), rgba(16,26,23,0))" }),
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d={sens < 0 ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"} />
      </svg>
    </button>
  );
}

const chevron: CSSProperties = {
  appearance: "none",
  position: "absolute",
  left: 0,
  right: 0,
  height: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  color: COULEURS.creme,
  cursor: "pointer",
  transition: "opacity .2s ease",
};
